import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { CUBOH_INTEGRATION_STATUS } from 'constants/cuboh';
import { INTEGRATION_NAME_MAP } from 'constants/reporting';
import { BADGE_VARIANTS } from 'constants/mantineVariants';
import { INTEGRATION_STATUS, MENU_TYPES } from 'constants/menuManagement';

import { getScheduledPublishDate } from 'helpers/menuManagementHours';
import { formatIntegrationPublishDateToMilliseconds } from 'helpers/menuManagement';

import { useMediaQuery } from '@mantine/hooks';
import { breakpoints } from 'styles/theme.variables';

import { Accordion, Badge, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useContext } from 'react';
import { HeadquarterContext } from 'context/HeadquarterContext';

function DataRow({ label, desc }) {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const width = isMobile ? 'var(--dimension-xl5)' : 'var(--dimension-xl7)';

  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <Text w={width} miw={width} variant="body-sm">
        {label}
      </Text>
      <Text variant="body-sm">{desc}</Text>
    </Group>
  );
}

DataRow.propTypes = {
  desc: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  label: PropTypes.string,
};

export default function MenuInfoModal({ opened, close, companies, menu = {}, merchants }) {
  const {
    companyId,
    integrations,
    merchantId,
    associatedMerchants,
    clonedMenu,
    masterMerchant,
    name,
  } = menu;

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const today = dayjs();

  const isMasterCopy = associatedMerchants?.length > 0;
  const isCateringMenu = menu.menuType === MENU_TYPES.CATERING;
  const isClonedMenu = clonedMenu;
  const isPublishScheduled = menu?.specialHours?.some(
    hours => !hours.isPublished && dayjs(hours.appliedDate).isAfter(today, 'day'),
  );
  const { isSingleMerchantType } = useContext(HeadquarterContext);

  let nextScheduledDate = '';
  if (isPublishScheduled) {
    const nextSpecialHours = (menu?.specialHours ?? [])
      .filter(hours => !hours.isPublished && dayjs(hours.appliedDate).isAfter(today, 'day'))
      .sort((a, b) => dayjs(a.appliedDate).diff(dayjs(b.appliedDate)))[0];

    if (nextSpecialHours) {
      const specialHoursDate = dayjs(nextSpecialHours.appliedDate);
      const publishDate = dayjs(getScheduledPublishDate(specialHoursDate));

      // only show if publish date is within next 3 weeks
      const showPublishMessage = publishDate.isBefore(today.add(3, 'week'));

      if (showPublishMessage) {
        // If calculated publish date is in the past, show "tonight"
        const displayDate = publishDate.isBefore(today)
          ? 'tonight'
          : publishDate.format('MMMM D, YYYY');

        // No matter where the restaurant is, we always publish
        // at 1am Pacific Time (where the server is located)
        nextScheduledDate = `This menu is scheduled to be published ${displayDate} at 1am PT because of upcoming Special/Holiday hours for ${specialHoursDate.format('MMMM D, YYYY')}.`;
      }
    }
  }

  function getNamesOfAssociatedMerchants() {
    const names = [];
    associatedMerchants.forEach(merchant => {
      const merchantData = merchants[merchant];
      const merchantName = merchantData?.name || merchant;
      const companyData = merchantData ? companies[merchantData.companyID] : null;
      names.push(`${companyData ? `${companyData.name} / ` : ''}${merchantName}`);
    });
    return names;
  }

  function getBadgeVariant(status) {
    switch (status) {
      case CUBOH_INTEGRATION_STATUS.inProgress:
        return 'blue';
      case CUBOH_INTEGRATION_STATUS.failed:
        return 'caution';
      case CUBOH_INTEGRATION_STATUS.modified:
        return 'yellow';
      case CUBOH_INTEGRATION_STATUS.published:
        return 'success';
      case CUBOH_INTEGRATION_STATUS.pulled:
        return 'outline';
      case CUBOH_INTEGRATION_STATUS.inactive:
        return 'gray';
      default:
        return null;
    }
  }

  function formatPublishDate(date) {
    return dayjs(formatIntegrationPublishDateToMilliseconds(date)).format('MM/DD/YYYY');
  }

  const hasOverviewContent =
    isMasterCopy || isCateringMenu || isClonedMenu || nextScheduledDate || !isSingleMerchantType;

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={`${name} Menu Info`}
      data-testid="menuInfoModal"
      size={isMobile ? 'sm' : 'lg'}
    >
      <Stack gap="md">
        {hasOverviewContent && (
          <Accordion defaultValue="overview" variant="bordered-card">
            <Accordion.Item value="overview">
              <Accordion.Control>
                <Text variant="title-sm">Overview</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  {isMasterCopy && (
                    <DataRow
                      label="Master"
                      desc="This is a master menu. Only HQ users can edit a master menu"
                    />
                  )}
                  {isCateringMenu && (
                    <DataRow
                      label="Catering"
                      desc="This is a catering menu. This menu does not support ASAP orders."
                    />
                  )}
                  {isClonedMenu && (
                    <DataRow
                      label="Cloned"
                      desc={`This is clone of a 'Master Menu'. Only HQ users can edit a master menu. Master
                is published to '${masterMerchant.menuName}'`}
                    />
                  )}
                  {nextScheduledDate && <DataRow label="Scheduled" desc={nextScheduledDate} />}
                  {!isSingleMerchantType && (
                    <DataRow
                      label="Merchants"
                      desc={
                        isMasterCopy && getNamesOfAssociatedMerchants()?.length ? (
                          <>
                            <Text inherit>
                              There {getNamesOfAssociatedMerchants()?.length > 1 ? 'are' : 'is'}{' '}
                              {getNamesOfAssociatedMerchants()?.length} merchant
                              {getNamesOfAssociatedMerchants()?.length > 1 ? 's' : ''} linked to
                              this menu:
                            </Text>
                            {getNamesOfAssociatedMerchants().map(merchant => (
                              <Text key={merchant} inherit>
                                {merchant}
                              </Text>
                            ))}
                          </>
                        ) : (
                          `${companies[companyId]?.name} / ${merchants[merchantId]?.name}`
                        )
                      }
                    />
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}
        {!!integrations?.length && (
          <Accordion defaultValue="publishTo" variant="bordered-card">
            <Accordion.Item value="publishTo">
              <Accordion.Control>
                <Text variant="title-sm">Publish to</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  {integrations.map(integration => (
                    <DataRow
                      key={`${integration.name}-${integration.status}`}
                      label={INTEGRATION_NAME_MAP[integration.integrationName]}
                      desc={
                        <Stack gap="xs2">
                          <Group gap="xs2">
                            <Badge variant={getBadgeVariant(integration.status)} size="xs">
                              {INTEGRATION_STATUS[integration.status].label}
                            </Badge>
                            {!isSingleMerchantType && (
                              <Badge variant={BADGE_VARIANTS.outline} size="xs">
                                {merchants[integration.merchantId]?.name}
                              </Badge>
                            )}
                          </Group>
                          {/* only show publish date if the menu has been published before */}
                          {integration.publishedDate > 0 && (
                            <Text inherit>
                              Last published on {formatPublishDate(integration.publishedDate)}
                            </Text>
                          )}
                          <Text inherit>{integration.pushPullSummary}</Text>
                        </Stack>
                      }
                    />
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}
      </Stack>
      <Button fullWidth mt="md" onClick={close}>
        Done
      </Button>
    </Modal>
  );
}

MenuInfoModal.propTypes = {
  close: PropTypes.func,
  companies: PropTypes.shape({}),
  menu: PropTypes.shape({
    associatedMerchants: PropTypes.arrayOf(PropTypes.string),
    clonedMenu: PropTypes.bool,
    companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    integrations: PropTypes.arrayOf(PropTypes.shape()),
    masterMerchant: PropTypes.shape({ menuName: PropTypes.string }),
    merchantId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    menuType: PropTypes.string,
    name: PropTypes.string,
    specialHours: PropTypes.arrayOf(PropTypes.shape()),
  }),
  merchants: PropTypes.shape({}),
  opened: PropTypes.bool,
};
